import { type Href, Redirect, router } from 'expo-router';
import { useAtom, useSetAtom } from 'jotai';
import { useState } from 'react';
import { FullScreenSpinner, useToastController } from 'smoothie';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import Screens from '../../common/types/Screens';
import { transformBillFromServer } from '../../common/utils/billUtils';
import LogRocketFacade from '../../logger/log-rocket/LogRocketFacade';
import { captureException } from '../../logger/Logger';
import useUpdateBill, { type UpdateBillBody } from '../hooks/useUpdateBill';
import { activeBillsAtom, selectedBillAtom } from '../state/BillsAtoms';
import type { IBill, PartialBill } from '../types/Bill';
import BillDetailsEditor from './BillDetailsEditor/BillDetailsEditor';

const EditBill = () => {
    LogRocketFacade.tagPage('EditBill');
    const toast = useToastController();

    const user = useAuthedUser();
    const { mutate: updateBill } = useUpdateBill();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [bill, setBill] = useAtom(selectedBillAtom);
    const storeActiveBills = useSetAtom(activeBillsAtom);
    if (!bill) {
        captureException(
            new Error('Attempted to load EditBill with no selected Bill'),
        );
        return <Redirect href="/bills/bills-overview" />;
    }

    const backToBillsOverview = () => {
        router.navigate({
            pathname: '/bills/bills-overview',
        });
    };

    const backToBill = () => {
        router.navigate(Screens.Bills.BillsHome as Href);
    };

    const onSubmit = (updatedBill: IBill) => {
        setIsSubmitting(true);

        const {
            id,
            accountId,
            displayName,
            payee,
            amount,
            cadence,
            timingLabel,
            timingDescription,
            category,
            lastPaidAt,
            memberMarkedAsSwitched,
        } = updatedBill;
        updateBill(
            {
                urlParams: {
                    id,
                    membershipId: user?.membership?.id ?? '',
                },
                body: {
                    accountId,
                    displayName,
                    payee,
                    amount,
                    cadence,
                    timingLabel,
                    timingDescription,
                    category,
                    lastPaidAt,
                    memberMarkedAsSwitched,
                } satisfies UpdateBillBody,
            },
            {
                onSuccess: (updatedBillFromServer) => {
                    const transformedBill = transformBillFromServer(
                        updatedBillFromServer,
                    );
                    storeActiveBills((prev) => {
                        return prev.map((pb) => {
                            return pb.id === transformedBill.id
                                ? transformedBill
                                : pb;
                        });
                    });
                    toast.show(`${payee} bill updated`);
                    setBill(transformedBill);
                    setIsSubmitting(false);
                    backToBillsOverview();
                },
                onError: (error) => {
                    toast.show('Something went wrong');
                    captureException(error);
                    setIsSubmitting(false);
                    // TODO handle error UX
                },
            },
        );
    };

    if (isSubmitting) {
        return <FullScreenSpinner />;
    }

    return (
        <BillDetailsEditor
            bill={bill satisfies PartialBill}
            title="Edit"
            submitButtonLabel="Update"
            onSubmit={onSubmit}
            onBack={backToBill}
        />
    );
};

export default EditBill;
